import { useState } from 'react';
import type { FormType } from '@/types/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Save, Settings, Tag, Bell } from 'lucide-react';

interface FormSettingsProps {
  form: FormType;
  onSave: (updates: Partial<FormType>) => void;
  onClose: () => void;
}

export const FormSettings: React.FC<FormSettingsProps> = ({
  form,
  onSave,
  onClose,
}) => {
  const [localForm, setLocalForm] = useState<FormType>(form);
  const [newTag, setNewTag] = useState('');

  const handleSave = () => {
    onSave(localForm);
    onClose();
  };

  const handleBasicUpdate = (key: keyof FormType, value: any) => {
    setLocalForm(prev => ({ ...prev, [key]: value }));
  };

  const handleMetadataUpdate = (key: string, value: any) => {
    setLocalForm(prev => ({
      ...prev,
      metadata: { ...prev.metadata, [key]: value }
    }));
  };

  const handleSettingsUpdate = (key: string, value: any) => {
    setLocalForm(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        settings: { ...prev.metadata.settings, [key]: value }
      }
    }));
  };

  const handleNotificationUpdate = (key: string, value: any) => {
    setLocalForm(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        settings: {
          ...prev.metadata.settings,
          notifications: { ...prev.metadata.settings.notifications, [key]: value }
        }
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !localForm.metadata.tags.includes(newTag.trim())) {
      handleMetadataUpdate('tags', [...localForm.metadata.tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    handleMetadataUpdate('tags', localForm.metadata.tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddRecipient = () => {
    const email = prompt('Enter email address:');
    if (email && email.includes('@')) {
      const currentRecipients = localForm.metadata.settings.notifications.recipients;
      if (!currentRecipients.includes(email)) {
        handleNotificationUpdate('recipients', [...currentRecipients, email]);
      }
    }
  };

  const handleRemoveRecipient = (emailToRemove: string) => {
    const currentRecipients = localForm.metadata.settings.notifications.recipients;
    handleNotificationUpdate('recipients', currentRecipients.filter(email => email !== emailToRemove));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="h-5 w-5" />
              <div>
                <h2 className="text-xl font-semibold">Form Settings</h2>
                <p className="text-sm text-muted-foreground">{form.name}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-140px)] p-6 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Form Name</label>
                <Input
                  value={localForm.name}
                  onChange={(e) => handleBasicUpdate('name', e.target.value)}
                  placeholder="Form name"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Description</label>
                <textarea
                  value={localForm.description}
                  onChange={(e) => handleBasicUpdate('description', e.target.value)}
                  placeholder="Form description"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <select
                    value={localForm.category}
                    onChange={(e) => handleBasicUpdate('category', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="report">Report</option>
                    <option value="project">Project</option>
                    <option value="survey">Survey</option>
                    <option value="application">Application</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Status</label>
                  <select
                    value={localForm.status}
                    onChange={(e) => handleBasicUpdate('status', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Version</label>
                <Input
                  value={localForm.version}
                  onChange={(e) => handleBasicUpdate('version', e.target.value)}
                  placeholder="1.0"
                />
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-3">
                {localForm.metadata.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag..."
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button onClick={handleAddTag} disabled={!newTag.trim()}>
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Form Behavior */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Form Behavior</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Allow Multiple Submissions</h4>
                  <p className="text-xs text-muted-foreground">Users can submit this form multiple times</p>
                </div>
                <input
                  type="checkbox"
                  checked={localForm.metadata.settings.allowMultipleSubmissions}
                  onChange={(e) => handleSettingsUpdate('allowMultipleSubmissions', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Require Authentication</h4>
                  <p className="text-xs text-muted-foreground">Users must be logged in to submit</p>
                </div>
                <input
                  type="checkbox"
                  checked={localForm.metadata.settings.requireAuthentication}
                  onChange={(e) => handleSettingsUpdate('requireAuthentication', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Enable Drafts</h4>
                  <p className="text-xs text-muted-foreground">Allow users to save drafts before submitting</p>
                </div>
                <input
                  type="checkbox"
                  checked={localForm.metadata.settings.enableDrafts}
                  onChange={(e) => handleSettingsUpdate('enableDrafts', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Email on Submit</h4>
                  <p className="text-xs text-muted-foreground">Send email when form is submitted</p>
                </div>
                <input
                  type="checkbox"
                  checked={localForm.metadata.settings.notifications.onSubmit}
                  onChange={(e) => handleNotificationUpdate('onSubmit', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              {localForm.metadata.settings.notifications.onSubmit && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Recipients</label>
                  <div className="space-y-2">
                    {localForm.metadata.settings.notifications.recipients.map((email) => (
                      <div key={email} className="flex items-center justify-between bg-muted p-2 rounded">
                        <span className="text-sm">{email}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveRecipient(email)}
                          className="h-6 w-6"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={handleAddRecipient}>
                      Add Recipient
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};